import { __awaiter, __generator } from 'tslib';
import { KpkApiMediaPermission } from '@keepeek/api-client';
import { getSignaturesSelector, serializeComplexeParamsForRecoil, thumbnailGenerationServiceConfigurationSelector, useCurrentUser } from '@keepeek/commons';
import { useRecoilCallback } from 'recoil';
import { getElementSourceLinkForImageServiceSelector } from '../providers/poi/selectors.js';
import { getResizeUrl } from '../providers/poi/utils.js';
import { getCropUrl } from './Crop/utils.js';

var useCroppedImageFromService = function () {
    return useRecoilCallback(function (_a) {
        var snapshot = _a.snapshot;
        return function (_a) {
            var outFormat = _a.outFormat, element = _a.element, outWidth = _a.outWidth, outHeight = _a.outHeight, cropWidth = _a.cropWidth, cropHeight = _a.cropHeight, cropX = _a.cropX, cropY = _a.cropY, downloadPrefixName = _a.downloadPrefixName, addExtensionAtEnd = _a.addExtensionAtEnd;
            return __awaiter(void 0, void 0, void 0, function () {
                var techProThumbnailGeneration, resourceSrc, signedUrls;
                return __generator(this, function (_b) {
                    switch (_b.label) {
                        case 0: return [4 /*yield*/, snapshot.getPromise(thumbnailGenerationServiceConfigurationSelector)];
                        case 1:
                            techProThumbnailGeneration = _b.sent();
                            return [4 /*yield*/, snapshot.getPromise(getElementSourceLinkForImageServiceSelector(serializeComplexeParamsForRecoil({ element: element })))];
                        case 2:
                            resourceSrc = _b.sent();
                            if (!resourceSrc) {
                                throw new Error("can't generate cropped image: source image for service is not valued");
                            }
                            return [4 /*yield*/, snapshot.getPromise(getSignaturesSelector({
                                    serviceId: "images",
                                    baseUrl: techProThumbnailGeneration.url,
                                    resourcePaths: [
                                        getCropUrl({
                                            cropWidth: cropWidth,
                                            cropHeight: cropHeight,
                                            outWidth: outWidth,
                                            outHeight: outHeight,
                                            cropX: cropX,
                                            cropY: cropY,
                                            format: outFormat,
                                            src: resourceSrc,
                                            downloadFilename: downloadPrefixName !== undefined
                                                ? "".concat(downloadPrefixName, "_").concat(outWidth || cropWidth, "x").concat(outHeight || cropHeight).replace(/[^a-zA-Z0-9]+/g, "_")
                                                : undefined,
                                            addExtensionAtEnd: addExtensionAtEnd
                                        }),
                                    ]
                                }))];
                        case 3:
                            signedUrls = _b.sent();
                            return [2 /*return*/, signedUrls[0]];
                    }
                });
            });
        };
    }, []);
};
var useResizeImageFromService = function () {
    return useRecoilCallback(function (_a) {
        var snapshot = _a.snapshot;
        return function (_a) {
            var poi = _a.poi, element = _a.element, resizeWidth = _a.resizeWidth, resizeHeight = _a.resizeHeight, resizeFormat = _a.resizeFormat, downloadPrefixName = _a.downloadPrefixName, addExtensionAtEnd = _a.addExtensionAtEnd;
            return __awaiter(void 0, void 0, void 0, function () {
                var thumbnailGenerationServiceConfiguration, resourceSrc, signedUrls;
                return __generator(this, function (_b) {
                    switch (_b.label) {
                        case 0:
                            if (!element) {
                                throw new Error("can't generate resize link: some mandatory informations are not valued");
                            }
                            return [4 /*yield*/, snapshot.getPromise(thumbnailGenerationServiceConfigurationSelector)];
                        case 1:
                            thumbnailGenerationServiceConfiguration = _b.sent();
                            return [4 /*yield*/, snapshot.getPromise(getElementSourceLinkForImageServiceSelector(serializeComplexeParamsForRecoil({ element: element })))];
                        case 2:
                            resourceSrc = _b.sent();
                            if (!thumbnailGenerationServiceConfiguration ||
                                !poi ||
                                !resourceSrc ||
                                !resizeWidth ||
                                !resizeHeight ||
                                !resizeFormat) {
                                throw new Error("can't generate resize link: some mandatory informations are not valued");
                            }
                            return [4 /*yield*/, snapshot.getPromise(getSignaturesSelector({
                                    serviceId: "images",
                                    baseUrl: thumbnailGenerationServiceConfiguration.url,
                                    resourcePaths: [
                                        getResizeUrl({
                                            outWidth: resizeWidth,
                                            outHeight: resizeHeight,
                                            format: resizeFormat,
                                            src: resourceSrc,
                                            poiX: poi.x,
                                            poiY: poi.y,
                                            downloadFilename: downloadPrefixName !== undefined
                                                ? "".concat(downloadPrefixName, "_").concat(resizeWidth, "x").concat(resizeHeight).replace(/[^a-zA-Z0-9]+/g, "_")
                                                : undefined,
                                            addExtensionAtEnd: addExtensionAtEnd
                                        }),
                                    ]
                                }))];
                        case 3:
                            signedUrls = _b.sent();
                            if (!signedUrls || signedUrls.length === 0) {
                                throw new Error("can't generate download link: signedUrls is empty");
                            }
                            return [2 /*return*/, signedUrls[0]];
                    }
                });
            });
        };
    });
};
var useUserDynameekRights = function (element) {
    var hasDynameekWriteAuthorization = useCurrentUser().hasDynameekWriteAuthorization;
    if (hasDynameekWriteAuthorization) {
        return {
            isCropAllowed: true,
            isTemplateAllowed: true,
            isPoiUpdateAllowed: true,
            isPoiUpdateOnElementAllowed: userRightOverRepertoryIsBiggerThanRead(element)
        };
    }
    else {
        return {
            isCropAllowed: false,
            isTemplateAllowed: false,
            isPoiUpdateAllowed: false,
            isPoiUpdateOnElementAllowed: false
        };
    }
};
var userRightOverRepertoryIsBiggerThanRead = function (element) {
    return (element === null || element === void 0 ? void 0 : element.permission) === KpkApiMediaPermission.Admin ||
        (element === null || element === void 0 ? void 0 : element.permission) === KpkApiMediaPermission.Write ||
        (element === null || element === void 0 ? void 0 : element.permission) === KpkApiMediaPermission.Delete ||
        (element === null || element === void 0 ? void 0 : element.permission) === KpkApiMediaPermission.Publish;
};

export { useCroppedImageFromService, useResizeImageFromService, useUserDynameekRights };
//# sourceMappingURL=hooks.js.map
